import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Stripe product and price IDs
export const SUBSCRIPTION_TIERS = {
  basic: {
    name: 'Basic Plan',
    price_id: 'price_1SorN1Rz2v7NUfnf83rmjIEQ',
    product_id: 'prod_TmQDhGcj8XupBU',
    price: 9.99,
  },
  pro: {
    name: 'Pro Plan',
    price_id: 'price_1SorNFRz2v7NUfnfv3oaOUY1',
    product_id: 'prod_TmQDI0vivY2imx',
    price: 29.99,
  },
  enterprise: {
    name: 'Enterprise Plan',
    price_id: 'price_1SorNaRz2v7NUfnf2Cv8XVpY',
    product_id: 'prod_TmQES0oj3oBely',
    price: 99.99,
  },
} as const;

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS | null;

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  loading: boolean;
  error: string | null;
}

export const useSubscription = () => {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    tier: null,
    subscriptionEnd: null,
    loading: true,
    error: null,
  });

  const checkSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setState(prev => ({ ...prev, loading: false, subscribed: false, tier: null }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Use tier directly from response if available, otherwise determine from product_id
      let tier: SubscriptionTier = null;
      if (data.subscribed) {
        if (data.tier && Object.keys(SUBSCRIPTION_TIERS).includes(data.tier)) {
          tier = data.tier as SubscriptionTier;
        } else if (data.product_id) {
          const foundTier = Object.entries(SUBSCRIPTION_TIERS).find(
            ([, tierData]) => tierData.product_id === data.product_id
          );
          tier = foundTier ? (foundTier[0] as SubscriptionTier) : null;
        }
      }

      setState({
        subscribed: data.subscribed,
        tier,
        subscriptionEnd: data.subscription_end,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error checking subscription:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to check subscription',
      }));
    }
  }, [session?.access_token]);

  const createCheckout = async (priceId: string) => {
    if (!session?.access_token) {
      throw new Error('You must be logged in to subscribe');
    }

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { priceId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;

    if (data?.url) {
      window.open(data.url, '_blank');
    }
  };

  const openCustomerPortal = async () => {
    if (!session?.access_token) {
      throw new Error('You must be logged in to manage subscription');
    }

    const { data, error } = await supabase.functions.invoke('customer-portal', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;

    if (data?.url) {
      window.open(data.url, '_blank');
    }
  };

  // Check subscription on mount and when user changes
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh subscription status every 60 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  // Refresh subscription when window gains focus (user returns from Stripe)
  useEffect(() => {
    if (!user) return;

    const handleFocus = () => {
      checkSubscription();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, checkSubscription]);

  return {
    ...state,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
  };
};
